import React from "react";
import { Box, Typography, Paper, Stack } from "@mui/material";

// Icons
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';

// Helpers & Sub-components
import { formatMoney } from "../../producer-dashboard/ProducerDashboardHelper";
import { RightArrow } from "./RightArrow";

type FundsRoutingGraphProps = {
    operatorName: string;
    escrowBal: number;
    scBalance: number;
    toProducers: number;
    toSC: number;
    prodToSC: number;
    awaitedRefill: number;
    reqRefill: number;
    awaitedPayout: number;
    currency: string;
};

export const FundsRoutingGraph = ({ 
    operatorName, escrowBal, scBalance, toProducers, toSC, prodToSC, awaitedRefill, reqRefill, awaitedPayout, currency 
}: FundsRoutingGraphProps) => {
    return (
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 4, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <AccountTreeIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '1px', fontSize: '0.65rem' }}>
                    HORIZONTAL ROUTING PREVIEW
                </Typography>
            </Stack>
            
            <Box sx={{ overflowX: 'auto', pb: 1, pt: 1 }}>
                <Box sx={{ position: 'relative', minWidth: 1020, height: 160, mx: 'auto' }}>
                    
                    <Box sx={{ position: 'absolute', left: 0, top: 0, width: 120, height: 60, p: 0.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #cbd5e1', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 2 }}>
                        <AccountBalanceOutlinedIcon sx={{ color: '#64748b', mx: 'auto', mb: 0.5, fontSize: 16 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.6rem' }}>BANK</Typography>
                        <Typography variant="body2" fontWeight={800} color="#0f172a" sx={{ fontSize: '0.75rem' }}>{operatorName}</Typography>
                    </Box>

                    <Box sx={{ position: 'absolute', left: 210, top: 0, width: 160, height: 140, p: 1, bgcolor: '#fff', borderRadius: 3, border: '2px solid #0f172a', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <AccountBalanceIcon sx={{ color: '#0f172a', mx: 'auto', mb: 0.5, fontSize: 20 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.65rem' }}>ESCROW WALLET</Typography>
                        <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ mt: 0.5 }}>
                            {escrowBal === -1 ? "API Error" : formatMoney(escrowBal, currency)}
                        </Typography>
                    </Box>

                    <Box sx={{ position: 'absolute', left: 460, top: 0, width: 160, height: 60, p: 1, bgcolor: '#fff', borderRadius: 2, border: '1px solid #fca5a5', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.6rem' }}>WALLET PRODUCERS</Typography>
                    </Box>

                    <Box sx={{ position: 'absolute', left: 660, top: 80, width: 160, height: 70, p: 1, bgcolor: '#eff6ff', borderRadius: 3, border: '2px solid #3b82f6', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <AccountBalanceWalletIcon sx={{ color: '#2563eb', mx: 'auto', mb: 0.5, fontSize: 18 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.6rem' }}>GLOBAL SC WALLET</Typography>
                        <Typography variant="body2" fontWeight={800} color="primary.main" sx={{ mt: 0.5 }}>
                            {scBalance === -1 ? "API Error" : formatMoney(scBalance, currency)}
                        </Typography>
                    </Box>

                    <Box sx={{ position: 'absolute', left: 900, top: 85, width: 120, height: 60, p: 0.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #cbd5e1', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 2 }}>
                        <AccountBalanceOutlinedIcon sx={{ color: '#64748b', mx: 'auto', mb: 0.5, fontSize: 16 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.6rem' }}>BANK (BE)</Typography>
                        <Typography variant="body2" fontWeight={800} color="#0f172a" sx={{ fontSize: '0.75rem' }}>Payout</Typography>
                    </Box>


                    {/* --- LIGNES ET FLÈCHES --- */}
                    
                    {/* Bank -> Escrow */}
                    <Box sx={{ position: 'absolute', left: 120, top: 20, width: 90, borderTop: '2px dashed #16a34a' }} />
                    <RightArrow left={204} top={18} color="#16a34a" />
                    <Typography sx={{ position: 'absolute', left: 120, top: -2, width: 90, textAlign: 'center', fontSize: '0.6rem', fontWeight: 800, color: '#16a34a' }}>+{formatMoney(reqRefill, currency)}</Typography>

                    <Box sx={{ position: 'absolute', left: 120, top: 40, width: 90, borderTop: '2px dashed #f59e0b' }} />
                    <RightArrow left={204} top={38} color="#f59e0b" />
                    <Typography sx={{ position: 'absolute', left: 120, top: 43, width: 90, textAlign: 'center', fontSize: '0.6rem', fontWeight: 800, color: '#f59e0b' }}>+{formatMoney(awaitedRefill, currency)}</Typography>

                    {/* Escrow -> Producer */}
                    <Box sx={{ position: 'absolute', left: 370, top: 30, width: 90, borderTop: '2px dashed #ef4444' }} />
                    <RightArrow left={454} top={28} color="#ef4444" />
                    <Typography sx={{ position: 'absolute', left: 370, top: 12, width: 90, textAlign: 'center', fontSize: '0.6rem', fontWeight: 800, color: '#ef4444' }}>-{formatMoney(toProducers, currency)}</Typography>

                    {/* Escrow -> SC */}
                    <Box sx={{ position: 'absolute', left: 370, top: 115, width: 290, borderTop: '2px dashed #3b82f6' }} />
                    <RightArrow left={654} top={113} color="#3b82f6" />
                    <Typography sx={{ position: 'absolute', left: 390, top: 100, fontSize: '0.6rem', fontWeight: 800, color: '#3b82f6', bgcolor: '#f8fafc', px: 0.5 }}>
                        +{formatMoney(toSC, currency)}
                    </Typography>

                    {/* Producer -> SC */}
                    <Box sx={{ position: 'absolute', left: 540, top: 60, height: 35, borderLeft: '2px dashed #3b82f6' }} />
                    <Box sx={{ position: 'absolute', left: 540, top: 95, width: 120, borderTop: '2px dashed #3b82f6' }} />
                    <RightArrow left={654} top={93} color="#3b82f6" />
                    <Typography sx={{ position: 'absolute', left: 550, top: 80, fontSize: '0.6rem', fontWeight: 800, color: '#3b82f6', bgcolor: '#f8fafc', px: 0.5 }}>
                        +{formatMoney(prodToSC, currency)}
                    </Typography>

                    {/* SC -> Bank OUT */}
                    <Box sx={{ position: 'absolute', left: 820, top: 115, width: 80, borderTop: '2px dashed #ef4444' }} />
                    <RightArrow left={894} top={113} color="#ef4444" />
                    <Typography sx={{ position: 'absolute', left: 820, top: 97, width: 80, textAlign: 'center', fontSize: '0.6rem', fontWeight: 800, color: '#ef4444' }}>-{formatMoney(awaitedPayout, currency)}</Typography>

                </Box>
            </Box>
        </Paper>
    );
};